import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Grid, Button,
  Alert, Autocomplete, CircularProgress, Container, Chip, ToggleButton, ToggleButtonGroup,
  LinearProgress, IconButton, useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Build as TechnicianIcon,
  CleaningServices as JanitorIcon,
  Computer as ITIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircleOutline as LowIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

// Priority configuration with colors and icons
const priorityConfig = {
  low: {
    color: '#4caf50',
    bgColor: '#e8f5e9',
    borderColor: '#a5d6a7',
    icon: <LowIcon />,
    labelEn: 'Low',
    labelEs: 'Baja'
  },
  medium: {
    color: '#2196f3',
    bgColor: '#e3f2fd',
    borderColor: '#90caf9',
    icon: <InfoIcon />,
    labelEn: 'Medium',
    labelEs: 'Media'
  },
  high: {
    color: '#ff9800',
    bgColor: '#fff3e0',
    borderColor: '#ffcc80',
    icon: <WarningIcon />,
    labelEn: 'High',
    labelEs: 'Alta'
  },
  emergency: {
    color: '#f44336',
    bgColor: '#ffebee',
    borderColor: '#ef9a9a',
    icon: <ErrorIcon />,
    labelEn: 'Emergency',
    labelEs: 'Emergencia'
  }
};

export default function PublicWorkOrder() {
  const { assetNumber, locationName } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    requested_by_name: '',
    assignment_type: 'technician',
    requester_email: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]); // [{file, preview}]
  const [uploadProgress, setUploadProgress] = useState(0);

  // Check asset category for showing appropriate assignment options
  const isFacilitiesAsset = selectedAsset?.category === 'Facilities';
  const isEquipmentAsset = selectedAsset?.category === 'Equipment';
  const showAssignmentOptions = isFacilitiesAsset || isEquipmentAsset;

  useEffect(() => {
    // Check for language parameter in URL
    const lng = searchParams.get('lng');
    if (lng && (lng === 'en' || lng === 'es')) {
      i18n.changeLanguage(lng);
    }
    loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName]);

  useEffect(() => {
    if (assetNumber && assets.length > 0) {
      const asset = assets.find(a => a.asset_number === assetNumber);
      if (asset) {
        setSelectedAsset(asset);
      }
    }
  }, [assetNumber, assets]);

  const loadAssets = async () => {
    try {
      const params = locationName ? { location: locationName } : {};
      const response = await api.get('/public/assets', { params });
      setAssets(response.data);
    } catch (err) {
      setError(t('publicWorkOrder.errorLoadAssets'));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setSelectedFiles(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const handleRemoveFile = (idx) => {
    setSelectedFiles(prev => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedAsset) {
      setError(t('publicWorkOrder.errorAssetRequired'));
      return;
    }

    if (!formData.title || !formData.requested_by_name) {
      setError(t('publicWorkOrder.errorFieldsRequired'));
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Use FormData to support file upload
      const submitData = new FormData();
      submitData.append('asset_id', selectedAsset.id);
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('priority', formData.priority);
      submitData.append('requested_by_name', formData.requested_by_name);
      submitData.append('assignment_type', formData.assignment_type);
      if (formData.requester_email) {
        submitData.append('requester_email', formData.requester_email);
      }
      for (const item of selectedFiles) {
        submitData.append('photos', item.file);
      }

      const response = await api.post('/public/work-order', submitData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setWorkOrderNumber(response.data.work_order_number);
      setSubmitted(true);
      setLoading(false);
      setUploadProgress(0);
      selectedFiles.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
    } catch (err) {
      setError(err.response?.data?.error || t('publicWorkOrder.errorCreateWorkOrder'));
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const getPriorityLabel = (key) => {
    return i18n.language === 'es' ? priorityConfig[key].labelEs : priorityConfig[key].labelEn;
  };

  if (submitted) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        py: 4
      }}>
        <Container maxWidth="sm">
          <Box sx={{ position: 'absolute', top: 70, right: 24 }}>
            <LanguageToggle />
          </Box>
          <Paper sx={{
            p: 5,
            mt: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            <Box sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: '#e8f5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
            </Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {t('publicWorkOrder.successTitle')}
            </Typography>
            <Chip
              label={workOrderNumber}
              sx={{
                fontSize: '1.2rem',
                py: 2.5,
                px: 2,
                mb: 2,
                bgcolor: '#f5f5f5',
                fontWeight: 'bold'
              }}
            />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('publicWorkOrder.successMessage')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setSubmitted(false);
                setWorkOrderNumber('');
                setFormData({
                  title: '',
                  description: '',
                  priority: 'medium',
                  requested_by_name: '',
                  assignment_type: 'technician',
                  requester_email: ''
                });
                setSelectedAsset(null);
                setSelectedFiles([]);
              }}
              sx={{
                borderRadius: 3,
                py: 1.5,
                px: 4,
                textTransform: 'none',
                fontSize: '1rem',
                bgcolor: '#1e3a5f',
                '&:hover': {
                  bgcolor: '#2d5a87',
                }
              }}
            >
              {t('publicWorkOrder.submitAnother')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
      py: 4
    }}>
      <Container maxWidth="md">
        <Box sx={{ position: 'absolute', top: 70, right: 24 }}>
          <LanguageToggle />
        </Box>

        <Paper sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }}>
          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {t('publicWorkOrder.pageTitle')}
            </Typography>
            {locationName && (
              <Chip
                label={locationName}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  mt: 1
                }}
              />
            )}
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
              {t('publicWorkOrder.subtitle')}
            </Typography>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Equipment Selection */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('publicWorkOrder.equipmentLabel')} *
                  </Typography>
                  <Autocomplete
                    options={assets}
                    getOptionLabel={(asset) => `${asset.asset_number} - ${asset.asset_description}`}
                    value={selectedAsset}
                    onChange={(event, newValue) => {
                      setSelectedAsset(newValue);
                      // Blur the input to dismiss keyboard on mobile after selection
                      if (newValue && document.activeElement) {
                        document.activeElement.blur();
                      }
                    }}
                    renderOption={(props, asset) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography fontWeight="bold">{asset.asset_number}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {asset.asset_description}
                          </Typography>
                          {asset.location && (
                            <Chip label={asset.location} size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={i18n.language === 'es' ? 'Buscar equipo...' : 'Search equipment...'}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Selected Asset Card */}
                {selectedAsset && (
                  <Grid item xs={12}>
                    <Paper sx={{
                      p: 2,
                      bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f8f9fa',
                      borderRadius: 3,
                      border: `2px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0'}`
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {selectedAsset.asset_number}
                          </Typography>
                          <Typography variant="body1" color="text.primary">
                            {selectedAsset.asset_description}
                          </Typography>
                          {selectedAsset.location && (
                            <Chip
                              label={selectedAsset.location}
                              size="small"
                              sx={{ mt: 1 }}
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {selectedAsset.category && (
                          <Chip
                            label={selectedAsset.category}
                            size="small"
                            color="secondary"
                          />
                        )}
                      </Box>

                      {/* Assignment Type Bubbles - For Facilities (Tech/Janitor) or Equipment (Tech/IT) */}
                      {showAssignmentOptions && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                            {i18n.language === 'es' ? 'Tipo de problema:' : 'Issue Type:'}
                          </Typography>
                          <ToggleButtonGroup
                            value={formData.assignment_type}
                            exclusive
                            onChange={(e, newValue) => {
                              if (newValue) {
                                setFormData({ ...formData, assignment_type: newValue });
                              }
                            }}
                            size="small"
                            sx={{ display: 'flex', gap: 1 }}
                          >
                            <ToggleButton
                              value="technician"
                              sx={{
                                borderRadius: '20px !important',
                                border: `1.5px solid ${isDark ? '#5b8fb9' : '#1e3a5f'} !important`,
                                color: isDark ? '#90caf9' : 'inherit',
                                py: 0.5,
                                px: 2,
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                '&.Mui-selected': {
                                  bgcolor: '#1e3a5f',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: '#2d5a87',
                                  }
                                },
                                '&:hover': {
                                  bgcolor: isDark ? 'rgba(30,58,95,0.3)' : '#e8eef4',
                                }
                              }}
                            >
                              <TechnicianIcon sx={{ mr: 0.5, fontSize: 18 }} />
                              {i18n.language === 'es' ? 'Problema de Mantenimiento' : 'Maintenance Issue'}
                            </ToggleButton>
                            {isFacilitiesAsset && (
                              <ToggleButton
                                value="janitor"
                                sx={{
                                  borderRadius: '20px !important',
                                  border: '1.5px solid #4caf50 !important',
                                  color: isDark ? '#81c784' : 'inherit',
                                  py: 0.5,
                                  px: 2,
                                  textTransform: 'none',
                                  fontSize: '0.8rem',
                                  '&.Mui-selected': {
                                    bgcolor: '#4caf50',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: '#66bb6a',
                                    }
                                  },
                                  '&:hover': {
                                    bgcolor: isDark ? 'rgba(76,175,80,0.15)' : '#e8f5e9',
                                  }
                                }}
                              >
                                <JanitorIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                {i18n.language === 'es' ? 'Solicitud de Limpieza' : 'Janitorial Request'}
                              </ToggleButton>
                            )}
                            {isEquipmentAsset && (
                              <ToggleButton
                                value="it_technician"
                                sx={{
                                  borderRadius: '20px !important',
                                  border: '1.5px solid #9c27b0 !important',
                                  color: isDark ? '#ce93d8' : 'inherit',
                                  py: 0.5,
                                  px: 2,
                                  textTransform: 'none',
                                  fontSize: '0.8rem',
                                  '&.Mui-selected': {
                                    bgcolor: '#9c27b0',
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: '#ab47bc',
                                    }
                                  },
                                  '&:hover': {
                                    bgcolor: isDark ? 'rgba(156,39,176,0.15)' : '#f3e5f5',
                                  }
                                }}
                              >
                                <ITIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                {i18n.language === 'es' ? 'Problema de IT/Computadora' : 'IT/Computer Issue'}
                              </ToggleButton>
                            )}
                          </ToggleButtonGroup>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                )}

                {/* Issue Title */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('publicWorkOrder.titleLabel')} *
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('publicWorkOrder.titlePlaceholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('publicWorkOrder.descriptionLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('publicWorkOrder.descriptionPlaceholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                {/* File Attachments */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {i18n.language === 'es' ? 'Adjuntar archivos (opcional)' : 'Attachments (optional)'}
                  </Typography>

                  <Box
                    sx={{
                      border: `2px dashed ${isDark ? 'rgba(255,255,255,0.25)' : '#bdbdbd'}`,
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        borderColor: isDark ? '#5b8fb9' : '#1e3a5f',
                        bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8f9fa'
                      }
                    }}
                    component="label"
                  >
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <AttachFileIcon sx={{ fontSize: 40, color: isDark ? 'rgba(255,255,255,0.5)' : '#9e9e9e' }} />
                    <Typography variant="body2" color="text.secondary">
                      {i18n.language === 'es'
                        ? 'Toca para adjuntar fotos o PDFs'
                        : 'Tap to attach photos or PDFs'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i18n.language === 'es' ? 'Imágenes y PDF (máx 10MB cada uno)' : 'Images & PDF (max 10MB each)'}
                    </Typography>
                  </Box>

                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedFiles.map((item, idx) => (
                        <Paper key={idx} sx={{ p: 1.5, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f8f9fa' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {item.preview ? (
                              <Box component="img" src={item.preview} alt="Preview" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1.5 }} />
                            ) : (
                              <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e0e0e0', borderRadius: 1.5 }}>
                                {item.file.type === 'application/pdf'
                                  ? <PdfIcon sx={{ fontSize: 24, color: '#ef5350' }} />
                                  : <ImageIcon sx={{ fontSize: 24, color: isDark ? 'rgba(255,255,255,0.5)' : '#757575' }} />}
                              </Box>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="medium" noWrap color="text.primary">{item.file.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{(item.file.size / 1024 / 1024).toFixed(2)} MB</Typography>
                            </Box>
                            <IconButton onClick={() => handleRemoveFile(idx)} size="small" sx={{ color: '#f44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Priority Selection */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('publicWorkOrder.priorityLabel')}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <Grid item xs={6} sm={3} key={key}>
                        <Paper
                          onClick={() => setFormData({ ...formData, priority: key })}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            borderRadius: 3,
                            border: `3px solid ${formData.priority === key ? config.color : 'transparent'}`,
                            bgcolor: formData.priority === key
                              ? (isDark ? `${config.color}22` : config.bgColor)
                              : (isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5'),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: isDark ? `${config.color}22` : config.bgColor,
                              transform: 'translateY(-2px)',
                              boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <Box sx={{
                            color: config.color,
                            mb: 1,
                            '& svg': { fontSize: 32 }
                          }}>
                            {config.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: formData.priority === key ? config.color : 'text.primary' }}
                          >
                            {getPriorityLabel(key)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Your Name */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('publicWorkOrder.nameLabel')} *
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.requested_by_name}
                    onChange={(e) => setFormData({ ...formData, requested_by_name: e.target.value })}
                    placeholder={i18n.language === 'es' ? 'Ingrese su nombre' : 'Enter your name'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                {/* Email for Confirmation */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {i18n.language === 'es' ? 'Email para confirmación (opcional)' : 'Email for confirmation (optional)'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={formData.requester_email}
                    onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
                    placeholder={i18n.language === 'es' ? 'su.email@ejemplo.com' : 'your.email@example.com'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                {/* Upload Progress */}
                {loading && uploadProgress > 0 && uploadProgress < 100 && (
                  <Grid item xs={12}>
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#1e3a5f',
                            borderRadius: 4
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                        {i18n.language === 'es' ? `Subiendo... ${uploadProgress}%` : `Uploading... ${uploadProgress}%`}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 2,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      bgcolor: '#1e3a5f',
                      boxShadow: '0 4px 15px rgba(30, 58, 95, 0.4)',
                      '&:hover': {
                        bgcolor: '#2d5a87',
                        boxShadow: '0 6px 20px rgba(30, 58, 95, 0.5)',
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('publicWorkOrder.submitButton')
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', mt: 3, color: 'rgba(255,255,255,0.7)' }}
        >
          Sleeptronic Maintenance System
        </Typography>
      </Container>
    </Box>
  );
}
